import React, { useState, useEffect } from 'react';
import { Container, Alert, Spinner } from 'react-bootstrap';
import feedbackAnalyticsService from '../../../services/feedback-analytics';
import type {
  FeedbackAnalyticsDto,
  AnalyticsFilter,
} from '../../../services/feedback-analytics';
import FeedbackFilters from './components/FeedbackFilters';
import OverviewCards from './components/OverviewCards';
import RatingDistributionChart from './components/RatingDistributionChart';
import TrendChart from './components/TrendChart';
import FormAnalyticsChart from './components/FormAnalyticsChart';
import ExportButtons from './components/ExportButtons';

interface DashboardState {
  data: FeedbackAnalyticsDto | null;
  loading: boolean;
  error: string | null;
  filters: AnalyticsFilter;
}

const FeedbackAnalyticsDashboard: React.FC = () => {
  console.log('[FeedbackAnalyticsDashboard] Render iniciado');
  const [state, setState] = useState<DashboardState>({
    data: null,
    loading: false,
    error: null,
    filters: {},
  });

  // Carrega dados iniciais
  useEffect(() => {
    console.log('[FeedbackAnalyticsDashboard] Componente montado, carregando analytics...');
    loadAnalytics({});
  }, []);

  const loadAnalytics = async (filters: AnalyticsFilter) => {
    console.log('[FeedbackAnalyticsDashboard] Iniciando requisição com filtros:', filters);
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      filters,
    }));

    try {
      const data = await feedbackAnalyticsService.getAnalytics(filters);
      console.log('[FeedbackAnalyticsDashboard] Dados recebidos:', data);
      setState((prev) => ({
        ...prev,
        data,
        loading: false,
      }));
    } catch (error: any) {
      console.error('[FeedbackAnalyticsDashboard] Erro ao carregar:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao carregar analytics';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const handleFilterChange = (filters: AnalyticsFilter) => {
    loadAnalytics(filters);
  };

  return (
    <Container fluid className="py-5 px-4">
      <div className="mb-5">
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#212529', marginBottom: '0.5rem' }}>
          Dashboard de Feedback
        </h1>
        <p style={{ color: '#6c757d' }}>
          Análise completa de avaliações de experiência do hóspede
        </p>
      </div>

      {/* Filtros */}
      <FeedbackFilters
        onFilterChange={handleFilterChange}
        loading={state.loading}
      />

      {/* Erro */}
      {state.error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setState(prev => ({...prev, error: null}))}>
          <Alert.Heading>Erro</Alert.Heading>
          <p>{state.error}</p>
        </Alert>
      )}

      {/* Conteúdo */}
      {state.loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="text-muted">Carregando dados...</p>
        </div>
      )}

      {!state.loading && (
        <>
          {/* Botões de Export */}
          {state.data && <ExportButtons data={state.data} loading={state.loading} />}

          {/* Visão Geral */}
          <OverviewCards data={state.data} loading={state.loading} />

          {/* Gráficos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
            {/* Distribuição de Notas */}
            <RatingDistributionChart
              data={state.data}
              loading={state.loading}
            />

            {/* Tendências */}
            {state.data && state.data.trendData.length > 1 && (
              <TrendChart data={state.data} loading={state.loading} />
            )}

            {/* Análise por Formulário */}
            <FormAnalyticsChart data={state.data} loading={state.loading} />
          </div>

          {/* Mensagem vazia */}
          {!state.data && (
            <Alert variant="info" className="text-center mt-8">
              <Alert.Heading>Nenhum dado disponível</Alert.Heading>
              <p>Nenhuma avaliação foi encontrada para o período selecionado.</p>
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default FeedbackAnalyticsDashboard;
